import { Log, IShortLogger } from './logger.service';

/**
 * Abstract base class for browser storage services.
 * Provides a unified interface for localStorage, sessionStorage, and other Storage implementations.
 *
 * @abstract
 * @class StorageService
 *
 * @example
 * ```typescript
 * // Extend for specific storage type
 * export class LocalStorageService extends StorageService {
 *   constructor() {
 *     super(localStorage);
 *   }
 * }
 * ```
 */
export abstract class StorageService {
  /**
   * The underlying Storage implementation (localStorage, sessionStorage, etc.)
   * @protected
   * @type {Storage}
   */
  protected storage!: Storage;

  /**
   * Creates an instance of StorageService.
   *
   * @param {Storage} storage - The Storage implementation to use (localStorage, sessionStorage, etc.)
   * @param {IShortLogger<any>} logger - Logger instance for this service
   */
  constructor(storage: Storage, protected readonly logger: IShortLogger<any>) {
    this.storage = storage;
  }

  /**
   * Store a value in the storage.
   * The value will be automatically JSON stringified.
   *
   * @template T - The type of the value to store
   * @param {string} key - The storage key
   * @param {T} value - The value to store (will be JSON stringified)
   * @returns {StorageService} Returns this instance for method chaining
   *
   * @example
   * ```typescript
   * service.setItem('user', { name: 'John', age: 30 });
   *
   * // Method chaining
   * service
   *   .setItem('user', userData)
   *   .setItem('settings', userSettings);
   * ```
   */
  public setItem<T>(key: string, value: T): StorageService {
    try {
      this.storage.setItem(key, JSON.stringify(value));
    } catch (error) {
      this.logger.e(`Failed to store item with key "${key}":`, error);
    }
    return this;
  }

  /**
   * Retrieve a value from the storage.
   * The value will be automatically JSON parsed.
   *
   * @template T - The expected type of the retrieved value
   * @param {string} key - The storage key
   * @returns {T | null} The parsed value or null if not found or parsing fails
   *
   * @example
   * ```typescript
   * const user: User | null = service.getItem<User>('user');
   * const settings = service.getItem<any>('settings');
   * ```
   */
  public getItem<T>(key: string): T | null {
    try {
      const item = this.storage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      this.logger.e(`Failed to retrieve item with key "${key}":`, error);
      return null;
    }
  }

  /**
   * Remove an item from the storage and return its value.
   * The returned value will be automatically JSON parsed.
   *
   * @template T - The expected type of the removed value
   * @param {string} key - The storage key
   * @returns {T | null} The parsed value that was removed, or null if not found or parsing fails
   *
   * @example
   * ```typescript
   * const removedUser: User | null = service.removeItem<User>('user');
   * ```
   */
  public removeItem<T>(key: string): T | null {
    try {
      const item = this.storage.getItem(key);
      this.storage.removeItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      this.logger.e(`Failed to remove item with key "${key}":`, error);
      return null;
    }
  }

  /**
   * Clear all items from the storage.
   *
   * @returns {StorageService} Returns this instance for method chaining
   *
   * @example
   * ```typescript
   * service.clear();
   * ```
   */
  public clear(): StorageService {
    try {
      this.storage.clear();
    } catch (error) {
      this.logger.e('Failed to clear storage:', error);
    }
    return this;
  }

  /**
   * Check if a key exists in the storage.
   *
   * @param {string} key - The storage key to check
   * @returns {boolean} True if the key exists, false otherwise
   *
   * @example
   * ```typescript
   * if (service.hasItem('user')) {
   *   const user = service.getItem<User>('user');
   * }
   * ```
   */
  public hasItem(key: string): boolean {
    return !!this.storage.getItem(key);
  }
}
